import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Linkedin, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Switch } from '@/Components/ui/switch';
import { Textarea } from '@/Components/ui/textarea';
import { cn } from '@/lib/utils';

import {
  linkedInAuthorize,
  linkedInDisconnect,
  postToLinkedIn,
  postToTelegram,
  telegramConnect,
  telegramDisconnect,
  telegramSetAutoPost,
} from './broadcastApi';
import { LINKEDIN_MESSAGE, LINKEDIN_RETURN_KEY } from './LinkedInCallback';

/**
 * ConnectedAccounts — post a job straight from the Broadcast panel:
 *
 *   LinkedIn   to the signed-in person's own profile feed (each person
 *              connects their own account; it is not a LinkedIn Jobs listing
 *              and not a company page post)
 *   Telegram   to the company's channel through the SearchJob.ai bot, on
 *              demand or automatically whenever a job is published
 *
 * A provider the server has not set up reports `configured: false` and is
 * left out, so nothing here appears until it can actually work.
 */

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export default function ConnectedAccounts({ jobId, social, live, canManage, onState, onReload }) {
  const linkedin = social?.linkedin;
  const telegram = social?.telegram;
  if (!linkedin?.configured && !telegram?.configured) return null;

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Post from connected accounts
      </h3>
      <div className="divide-y divide-border rounded-xl border border-border bg-card">
        {linkedin?.configured && (
          <LinkedInCard jobId={jobId} linkedin={linkedin} live={live} onState={onState} onReload={onReload} />
        )}
        {telegram?.configured && (
          <TelegramCard
            jobId={jobId}
            telegram={telegram}
            live={live}
            canManage={canManage}
            onState={onState}
            onReload={onReload}
          />
        )}
      </div>
    </section>
  );
}

function PostStatus({ post, noun }) {
  if (!post) return null;
  if (post.status === 'posted') {
    return (
      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-emerald-700">
        <span className="inline-flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Posted {timeAgo(post.at)}
        </span>
        {post.url && (
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
          >
            View {noun}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {post.error && <span className="text-amber-700">Last retry failed: {post.error}</span>}
      </p>
    );
  }
  return (
    <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>Not posted: {post.error || 'the last attempt failed.'}</span>
    </p>
  );
}

function CardShell({ icon: Icon, tone, title, subtitle, action, children }) {
  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/5">
          <Icon className={cn('h-4 w-4', tone)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{title}</p>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
            {action}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LinkedIn
// ---------------------------------------------------------------------------

function LinkedInCard({ jobId, linkedin, live, onState, onReload }) {
  const [busy, setBusy] = useState(null); // 'connect' | 'post' | 'disconnect'
  const [composing, setComposing] = useState(false);
  const [text, setText] = useState(linkedin.draft || '');
  const popupRef = useRef(null);

  // The callback page (in the popup) reports back with postMessage.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== LINKEDIN_MESSAGE) return;
      setBusy(null);
      if (event.data.ok) {
        toast.success('LinkedIn connected.');
        onReload();
      } else {
        toast.error(event.data.error || 'LinkedIn was not connected.');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onReload]);

  // If the user just closes the popup, stop showing "Connecting…".
  useEffect(() => {
    if (busy !== 'connect') return undefined;
    const timer = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        popupRef.current = null;
        setBusy(null);
        onReload();
      }
    }, 800);
    return () => clearInterval(timer);
  }, [busy, onReload]);

  const connect = async () => {
    // Open the window during the click itself so popup blockers allow it,
    // then point it at LinkedIn once the URL is known.
    const popup = window.open('', 'sj-linkedin-connect', 'width=600,height=720');
    popupRef.current = popup;
    setBusy('connect');
    try {
      const { url } = await linkedInAuthorize();
      if (popup) {
        popup.location.href = url;
      } else {
        // Popup blocked: use this tab and come back here afterwards.
        try {
          sessionStorage.setItem(LINKEDIN_RETURN_KEY, window.location.pathname + window.location.search);
        } catch (e) {
          /* storage unavailable */
        }
        window.location.href = url;
      }
    } catch (e) {
      if (popup) popup.close();
      popupRef.current = null;
      setBusy(null);
      toast.error(e.message);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await linkedInDisconnect();
      toast.success('LinkedIn disconnected.');
      setComposing(false);
      onReload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const post = async () => {
    setBusy('post');
    try {
      onState(await postToLinkedIn(jobId, text));
      setComposing(false);
      toast.success('Posted to your LinkedIn profile.');
    } catch (e) {
      toast.error(e.message);
      onReload(); // shows the recorded failure, or the expired connection
    } finally {
      setBusy(null);
    }
  };

  const posted = linkedin.post?.status === 'posted';
  const remaining = (linkedin.max_chars || 3000) - text.length;

  let action = null;
  if (linkedin.connected) {
    action = !composing && (
      <Button
        size="sm"
        onClick={() => {
          setText(linkedin.draft || '');
          setComposing(true);
        }}
        disabled={!live || busy !== null}
        title={live ? undefined : 'Publish the job first'}
      >
        {posted ? 'Post again' : 'Post to LinkedIn'}
      </Button>
    );
  } else {
    action = (
      <Button size="sm" variant={linkedin.expired ? 'default' : 'outline'} onClick={connect} disabled={busy !== null}>
        {busy === 'connect' && <Loader2 className="h-4 w-4 animate-spin" />}
        {linkedin.expired ? 'Reconnect' : 'Connect LinkedIn'}
      </Button>
    );
  }

  return (
    <CardShell
      icon={Linkedin}
      tone="text-[#0A66C2]"
      title="LinkedIn"
      subtitle={
        linkedin.connected
          ? `Connected as ${linkedin.name || 'your account'}. Posts go to your own profile.`
          : linkedin.expired
          ? 'Your LinkedIn connection has expired. LinkedIn asks you to sign in again about every two months.'
          : 'Connect your account to post this job to your own profile in one click.'
      }
      action={action}
    >
      {!linkedin.connected && (
        <p className="mt-1 text-xs text-muted-foreground">
          This is a post on your personal feed, not a LinkedIn Jobs listing or a company page post.
        </p>
      )}
      <PostStatus post={linkedin.post} noun="post" />

      {composing && (
        <div className="mt-3 space-y-2">
          <Label htmlFor="linkedin-post-text" className="text-xs text-muted-foreground">
            Post text
          </Label>
          <Textarea
            id="linkedin-post-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            className="text-sm"
            disabled={busy === 'post'}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={cn('text-xs', remaining < 0 ? 'text-destructive' : 'text-muted-foreground')}>
              {remaining < 0
                ? `${-remaining} characters over LinkedIn's limit.`
                : 'Posted publicly on your profile with a link to the job.'}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setComposing(false)} disabled={busy === 'post'}>
                Cancel
              </Button>
              <Button size="sm" onClick={post} disabled={busy !== null || remaining < 0 || !text.trim()}>
                {busy === 'post' && <Loader2 className="h-4 w-4 animate-spin" />}
                Post now
              </Button>
            </div>
          </div>
        </div>
      )}

      {linkedin.connected && !composing && (
        <button
          type="button"
          onClick={disconnect}
          disabled={busy !== null}
          className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          Disconnect LinkedIn
        </button>
      )}
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Telegram
// ---------------------------------------------------------------------------

function TelegramCard({ jobId, telegram, live, canManage, onState, onReload }) {
  const [busy, setBusy] = useState(null); // 'connect' | 'post' | 'auto' | 'disconnect'
  const [channel, setChannel] = useState('');
  const [error, setError] = useState('');

  const run = async (key, fn, success) => {
    setBusy(key);
    setError('');
    try {
      onState(await fn());
      if (success) toast.success(success);
      return true;
    } catch (e) {
      if (key === 'connect') setError(e.message);
      else toast.error(e.message);
      if (key === 'post') onReload(); // shows the recorded failure
      return false;
    } finally {
      setBusy(null);
    }
  };

  const connect = async (e) => {
    e.preventDefault();
    if (await run('connect', () => telegramConnect(jobId, channel), 'Telegram channel connected.')) {
      setChannel('');
    }
  };

  const bot = telegram.bot_username ? `@${telegram.bot_username}` : 'the SearchJob.ai bot';
  const posted = telegram.post?.status === 'posted';

  if (!telegram.connected) {
    return (
      <CardShell
        icon={Send}
        tone="text-sky-500"
        title="Telegram channel"
        subtitle={
          canManage
            ? "Post your jobs to your company's Telegram channel."
            : 'No channel is connected. The company account can connect one.'
        }
      >
        {canManage && (
          <form onSubmit={connect} className="mt-3 space-y-2">
            <ol className="list-decimal space-y-0.5 pl-4 text-xs text-muted-foreground">
              <li>
                In Telegram, add <span className="font-medium text-foreground">{bot}</span> to your
                channel as an administrator with permission to post messages.
              </li>
              <li>Enter the channel's @username below.</li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <Input
                value={channel}
                onChange={(e) => {
                  setChannel(e.target.value);
                  setError('');
                }}
                placeholder="@yourcompany_jobs"
                aria-label="Telegram channel username"
                className="h-9 min-w-0 flex-1"
                disabled={busy !== null}
              />
              <Button type="submit" size="sm" className="h-9" disabled={busy !== null || !channel.trim()}>
                {busy === 'connect' && <Loader2 className="h-4 w-4 animate-spin" />}
                Connect
              </Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </form>
        )}
      </CardShell>
    );
  }

  const name = [telegram.channel?.title, telegram.channel?.handle].filter(Boolean).join(' · ');

  return (
    <CardShell
      icon={Send}
      tone="text-sky-500"
      title="Telegram channel"
      subtitle={`Connected to ${name || 'your channel'}.`}
      action={
        canManage && (
          <Button
            size="sm"
            onClick={() => run('post', () => postToTelegram(jobId), 'Posted to your Telegram channel.')}
            disabled={!live || busy !== null}
            title={live ? undefined : 'Publish the job first'}
          >
            {busy === 'post' && <Loader2 className="h-4 w-4 animate-spin" />}
            {posted ? 'Post again' : 'Post now'}
          </Button>
        )
      }
    >
      <PostStatus post={telegram.post} noun="message" />

      <div className="mt-3 flex items-center justify-between gap-3">
        <Label htmlFor="telegram-auto-post" className="text-xs font-normal text-foreground">
          Post every job automatically when it is published
        </Label>
        <Switch
          id="telegram-auto-post"
          checked={Boolean(telegram.auto_post)}
          disabled={!canManage || busy !== null}
          onCheckedChange={(next) => run('auto', () => telegramSetAutoPost(jobId, next))}
        />
      </div>

      {canManage && (
        <button
          type="button"
          onClick={() => run('disconnect', () => telegramDisconnect(jobId), 'Telegram channel disconnected.')}
          disabled={busy !== null}
          className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          Disconnect channel
        </button>
      )}
    </CardShell>
  );
}
